/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'underscore'
], function(Class, _) {

	/**
	 * list of properties publicly exposed as part of the filter entry
	 */
	const FILTERENTRY_PROPS = [
		'id',
		'sourceId',
		'dataType',
		'usage',
		'origin',
		'scope',
		'hierarchyUniqueNames',
		'tupleSet',
		'exclude'
	];

	/**
	 * @class FilterApi
	 * @hideconstructor
	 * @classdesc API class that is used to control dashboard filters.
	 * <strong>FilterApi</strong> can not be instantiated directly.
	 * <strong>FilterApi</strong> can be obtained through the <strong>{@link Api}</strong> by invoking <tt>getFilters</tt>.
	 */
	return class FilterApi {
		// Public APIs
		static get API_METHODS() {
			return [
				'addFilter',
				'editFilter',
				'removeFilter',
				'removeFilters',
				'clearFilters'
			];
		}

		constructor (controller, dashboardAPI) {
			this.controller = controller;
			this.dashboardAPI = dashboardAPI;
		}

		/**
		 * Add a filter to the dashboard
		 * @public
		 * @async
		 * @function FilterApi#addFilter
		 * @param {Object} column object describing a filter metadata column object
		 * @param {string} column.sourceId Datasource of metadata column
		 * @param {string} column.itemId metadata column identfier
		 * @param {Object} [options] Filter options object
		 * @param {Object[]} [options.values = []] Array of values applied to the filter
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @param {boolean} [options.openViewOnLoad = false] Flag indicating whether to open the filter dialog
		 * @example
		 *	filters.addFilter({
		 *		sourceId: 'model000001648e892ad6_00000001',
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		values: [{
		 *			u: 'sales_and_marketing_csv.Year_->[2006]',
		 *			d: '2006'
		 *		}],
		 *		scope: 'global',
		 *		openViewOnLoad: false,
		 *		exclude: false
		 *	});
		 * @returns {Promise<Object>} filter entry object
		 */
		addFilter(column, options) {
			return this._updateFilter('update', column, options);
		}

		/**
		 * Edit an existing filter to the dashboard
		 * @public
		 * @async
		 * @function FilterApi#editFilter
		 * @param {Object} column object describing a filter metadata column object
		 * @param {string} column.sourceId Datasource of metadata column
		 * @param {string} column.itemId metadata column identfier
		 * @param {Object} [options] Filter options object
		 * @param {Object[]} [options.values = []] Array of values applied to the filter
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @param {boolean} [options.openViewOnLoad = false] Flag indicating whether to open the filter dialog
		 * @example
		 *	filters.editFilter({
		 *		sourceId: 'model000001648e892ad6_00000001',
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		values: [{
		 *			u: 'sales_and_marketing_csv.Year_->[2004]',
		 *			d: '2004'
		 *		}, {
		 *			u: 'sales_and_marketing_csv.Year_->[2006]',
		 *			d: '2006'
		 *		}],
		 *		scope: 'model000001648e89243b_00000001',
		 *		openViewOnLoad: false
		 *	});
		 * @returns {Promise<Object>} filter entry object
		 */
		editFilter(column, options) {
			return this._updateFilter('replace', column, options);
		}

		/**
		 * Remove an existing filter from the dashboard
		 * @public
		 * @async
		 * @function FilterApi#removeFilter
		 * @param {Object} column object describing a filter metadata column object
		 * @param {string} column.sourceId Datasource of metadata column
		 * @param {string} column.itemId metadata column identfier
		 * @param {Object} [options] Filter options object
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @example
		 *	filters.removeFilter({
		 *		sourceId: 'model000001648e892ad6_00000001',
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		scope: 'global'
		 *	});
		 * @returns {Promise<boolean>} true if successfully removed, otherwise false
		 */
		removeFilter(item, options) {
			const pageContext = this._getPageContextApi();
			const pageContextSelector = this._getPageContextSelector(item, options);
			const pageContextItem = pageContext.getPageContextItem(pageContextSelector);
			if (pageContextItem) {
				const deleteItem = this._getDeleteItemSpec(pageContextItem, options);
				const filterSpec = this._getFilterSpec('delete', options);
				return pageContext.updateFilterContext(deleteItem, filterSpec, options);
			} else {
				return false;
			}
		}

		/**
		 * Remove all existing filters from the dashboard matching the given selectors
		 * @public
		 * @async
		 * @function FilterApi#removeFilters
		 * @param {Object} selectors Object describing the filters to remove, if not specified, all filters will be removed
		 * @param {Object} [options] Object giving the remove filter options (e.g. undoRedoTransactionId)
		 * @example
		 *	filterApi.removeFilters({
		 *		scope: 'global' // all tabs
		 *	}, {
		 *		undoRedoTransactionId: '123'
		 *	});
		 * @returns {Promise<boolean>} true if any filters were successfully removed
		 */
		removeFilters(selectors, options) {
			return this._getPageContextApi().deletePageContextItems(selectors, options);
		}

		/**
		 * Clear all existing filter values from the dashboard matching the given selectors
		 * @public
		 * @async
		 * @function FilterApi#clearFilters
		 * @param {Object} selectors Object describing the filters to clear of values
		 * @param {Object} [options] Object giving the clear filter options (e.g. undoRedoTransactionId)
		 * @example
		 *	filterApi.clearFilters({
		 *		scope: 'model0000016428911c88_00000001' // single tab
		 *	}, {
		 *		undoRedoTransactionId: '123',
		 *		silent: true
		 *	});
		 * @returns {Promise<boolean>} true if any filter values were successfully cleared
		 */
		clearFilters(selectors, options) {
			return this._getPageContextApi().clearPageContextItemValues(selectors, options);
		}

		/**
		 * Add or edit a filter
		 * @private
		 * @async
		 * @function _updateFilter
		 * @param {Object} column object describing a filter metadata column object
		 * @param {string} column.sourceId Datasource of metadata column
		 * @param {string} column.itemId metadata column identfier
		 * @param {Object} [options] Filter options object
		 * @param {Object[]} [options.values = []] Array of values applied to the filter
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @param {boolean} [options.openViewOnLoad = false] Flag indicating whether to open the filter dialog
		 * @returns {Promise<Object>} Page Context API
		 */
		_updateFilter(command, column, options) {
			return this._getFilterMetadataColumn(column.sourceId, column.itemId).then(metaDataColumn => {
				const filterItem = this._getFilterItemSpec(metaDataColumn, options);
				const filterSpec = this._getFilterSpec(command, options);
				const filterEntry = this._getPageContextApi().updateFilterContext(filterItem, filterSpec, options);
				return _.pick(filterEntry, FILTERENTRY_PROPS);
			});
		}

		/**
		 * Get the Page Context API of the PageContextService
		 * @private
		 * @async
		 * @function _getPageContextApi
		 * @returns {Promise<Object>} Page Context API
		 */
		_getPageContextApi() {
			return  this.dashboardAPI.getFeature('PageContextService').getPageContext();
		}

		/**
		 * Get the list of all data sources
		 * @private
		 * @async
		 * @function _getSources
		 * @returns {Promise<Object[]>} array of all datasources
		 */
		_getSources(){
			const dataSourcesSvc = this.dashboardAPI.getFeature('dataSources.deprecated');
			const dataSourceCollection = dataSourcesSvc.getSourcesCollection();
			return Promise.resolve(dataSourceCollection ? dataSourceCollection.getSources() : []);
		}

		/**
		 * Get a data source with the give source id
		 * @private
		 * @async
		 * @function _getSourceById
		 * @param {string} id source identifier
		 * @returns {Promise<Object>} a datasource which matches the give source id
		 */
		_getSourceById(id) {
			return this._getSources().then(sources => {
				return _.find(sources, source => {
					return source.getId() === id;
				});
			});
		}

		/**
		 * Get a filter metadata column with the given source and column id
		 * @private
		 * @async
		 * @function _getFilterMetadataColumn
		 * @param {string} sourceId	metadata source identifier
		 * @param {string} id	metadata column identifier
		 * @returns {Promise<Object>} metadata column of the give source and column id
		 */
		_getFilterMetadataColumn(sourceId, id) {
			return this._getSourceById(sourceId).then(source => {
				return source.getModule().then(module => {
					return module.getMetadataColumn(id);
				});
			});
		}

		/**
		 * Generate the selector spec for selecting a page context item
		 * The selector is used for calling <tt>PageContextApi.getPageContextItem</tt>
		 * @private
		 * @function _getPageContextSelector
		 * @param {Object} item object describing a filter data item object
		 * @param {string} item.sourceId Datasource of data item
		 * @param {string} item.itemId data item identfier
		 * @param {Object} [options] Filter options object
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @returns {Object} page context selector object
		 */
		_getPageContextSelector(item, options) {
			return {
				itemId: item.itemId,
				scope: (options && options.scope) || 'global'
			};
		}

		/**
		 * Generate a filter item spec for add / edit, based on the given metadata column
		 * @private
		 * @function _getFilterItemSpec
		 * @param {Object} column metadata column used for the filter
		 * @param {Object} [options] options object which describes the details of the filter
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @param {boolean} [options.openViewOnLoad = false] Flag indicating whether to open the filter dialog
		 * @returns {Object} filter item spec
		 */
		_getFilterItemSpec(column, options) {
			return {
				sourceId: column.getSourceId(),
				itemId: column.getId(),
				itemName: column.getLabel(),
				dataType: column.getDataType(),
				usage: column.getType(),
				isHierarchy: column.isHierarchy(),
				isNamedSet: column.isNamedSet(),
				scope: (options && options.scope) || 'global',
				openViewOnLoad: (options && options.openViewOnLoad) || false
			};
		}

		/**
		 * Generate a filter item spec for delete, based on the given <tt>PageContextEntry</tt>
		 * @private
		 * @function _getDeleteItemSpec
		 * @param {Object} pageContextEntry PageContextEntry to delete
		 * @param {Object} [options] options object which describes the details of the filter to delete
		 * @param {string} [options.scope = 'global'] Scope of the filter. Identifier of tab which the filter applies to.
		 * Otherwise, if omitted or 'global' applies the filter to the entire dashboard
		 * @returns the delete filter item spec
		 */
		_getDeleteItemSpec(pageContextEntry, options) {
			return {
				item: pageContextEntry,
				scope: (options && options.scope) || 'global',
				origin: 'filter'
			};
		}

		/**
		 * Generate a filter spec with the given command
		 * @private
		 * @function _getFilterSpec
		 * @param {string} [cmd = 'update'] Allowed commands are 'update', 'replace' and 'delete'
		 * @param {Object} [options] options object which describes the details of the filter
		 * @param {Object[]} [options.values] Array of values applied to the filter
		 * @param {Object[]} [options.exclude] Flag indicating if the filter should be inverted
		 * @param {Object[]} [options.condition] Range condition of the filter
		 */
		_getFilterSpec(cmd, options) {

			let spec = {
				command: cmd || 'update',
				exclude: !!(options && options.exclude)
			};

			if (options && options.values) {
				spec = _.extend(spec, {
					values: options.values
				});
			} else if (options && options.condition) {
				spec = _.extend(spec, {
					condition: options.condition
				});
			}
			return spec;
		}
	};
});
